import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import Modal from "../modal";
import StarTierButton from "../starTierButton";

import categoryService from "../../services/category";
import { FiltersContext } from "../../services/context/filters";

import categoriesIcons from "../../assets/CategoriesIcons/index";

import "./filterModalStyles.css";

const TIME_OPTIONS = {
  STARTED: "STARTED",
  START_TODAY: "START_TODAY",
  START_THIS_WEEK: "START_THIS_WEEK",
  START_THIS_MONTH: "START_THIS_MONTH",
};

const FilterModal = ({
  displayModal,
  setDisplayModal,
  showPriceFilter,
  showTimeFilter,
}) => {
  const { setFilters } = useContext(FiltersContext);
  const [tier, setTier] = useState(0);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  // TODO: Think for the best approach for validating that the maximum price is not lower than de minimum price
  const [minPrice, setMinPrice] = useState(0);
  const [hasMinPriceError, setHasMinPriceError] = useState(false);
  const [maxPrice, setMaxPrice] = useState(0);
  const [hasMaxPriceError, setHasMaxPriceError] = useState(false);

  const inputMinPrice = useRef(null);
  const inputMaxPrice = useRef(null);

  const getCategories = useCallback(async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (categories.length === 0) {
      getCategories();
    }
  }, [categories.length, getCategories]);

  const isCategorySelected = (category) =>
    selectedCategories.find((cat) => cat === category);

  const onClickCategory = (category) => {
    const selected = isCategorySelected(category);

    selected
      ? setSelectedCategories(
          selectedCategories.filter((cat) => cat !== category)
        )
      : setSelectedCategories((oldCategories) => [...oldCategories, category]);
  };

  const onKeyPressPrice = (e) => !/[0-9]/.test(e.key) && e.preventDefault();

  const onChangePrice = (event, isMax) => {
    const value = event?.target?.value;
    if (!value || isNaN(value) || parseInt(value) <= 0) {
      isMax ? setHasMaxPriceError(true) : setHasMinPriceError(true);
      return;
    }

    if (isMax) {
      setHasMaxPriceError(false);
      setMaxPrice(value);
    } else {
      setHasMinPriceError(false);
      setMinPrice(value);
    }
  };

  const onChangeTime = (event) => {
    setSelectedTime(event.target.value);
  };

  const reset = () => {
    setSelectedCategories([]);
    setTier(0);

    if (showPriceFilter && showTimeFilter) {
      setSelectedTime("");

      setMinPrice(0);
      setMaxPrice(0);

      setHasMinPriceError(false);
      setHasMaxPriceError(false);
      inputMinPrice.current.value = null;
      inputMaxPrice.current.value = null;
    }
  };

  const onClickApplyChanges = () => {
    let filters = {};
    const categoriesIds = categories.reduce((ids, cat) => {
      const categoryName = cat.nameOffChain.replace(/\s+/g, "").toLowerCase();
      if (selectedCategories.includes(categoryName)) {
        ids.push(cat._id);
      }
      return ids;
    }, []);

    if (tier > 0) {
      filters = {
        ...filters,
        tier,
      };
    }

    if (categoriesIds.length > 0) {
      filters = {
        ...filters,
        category: categoriesIds,
      };
    }

    if (minPrice > 0) {
      filters = {
        ...filters,
        minPrice,
      };
    }

    if (maxPrice > 0) {
      filters = {
        ...filters,
        maxPrice,
      };
    }

    const minStart = new Date();
    const maxStart = new Date();
    switch (selectedTime) {
      case TIME_OPTIONS.STARTED:
        filters = {
          ...filters,
          maxStartDate: maxStart,
        };
        break;

      case TIME_OPTIONS.START_TODAY:
        maxStart.setUTCHours(23, 59, 59, 999);
        filters = {
          ...filters,
          minStartDate: minStart,
          maxStartDate: maxStart,
        };
        break;

      case TIME_OPTIONS.START_THIS_WEEK:
        maxStart.setDate(maxStart.getDate() + 7);
        filters = {
          ...filters,
          minStartDate: minStart,
          maxStartDate: maxStart,
        };
        break;

      case TIME_OPTIONS.START_THIS_MONTH:
        maxStart.setDate(maxStart.getDate() + 30);
        filters = {
          ...filters,
          minStartDate: minStart,
          maxStartDate: maxStart,
        };
        break;

      default:
        break;
    }

    setFilters(filters);
    setDisplayModal(false);
  };

  const getBodyContent = () => (
    <Container className="filter-modal">
      <Row className="align-items-center">
        <Col xs={12} lg={showPriceFilter && showTimeFilter ? 8 : 12}>
          <Container className="tier-category-container py-3 mt-2 mb-3">
            <Row>
              <Col xs={12}>
                <Row className="justify-content-center">
                  <span className="title">Tier</span>
                </Row>
                <Row className="justify-content-center mb-3">
                  <StarTierButton
                    amountStars={4}
                    tier={tier}
                    setTier={setTier}
                  />
                </Row>
                <Row className="justify-content-center">
                  <span className="title">Category</span>
                </Row>
                <Row className="categories justify-content-center">
                  {Object.entries(categoriesIcons).map(
                    ([key, value], index) => (
                      <div
                        key={index}
                        className={`background mt-2 mx-1 ${
                          isCategorySelected(key) && "selected"
                        }`}
                      >
                        <button
                          className={isCategorySelected(key) && "selected"}
                          onClick={() => onClickCategory(key)}
                        >
                          <img src={value} alt="category" />
                        </button>
                      </div>
                    )
                  )}
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
        {showPriceFilter && showTimeFilter && (
          <Col xs={12} lg={4}>
            <Row className="align-items-center">
              <Col xs={6} lg={12} className="price-col">
                <Container className="price-container py-3 mb-3">
                  <Row className="justify-content-center">
                    <span className="title">Price</span>
                  </Row>
                  <Row className="content-price align-items-center justify-content-center">
                    <Col xs={12} sm={2}>
                      <span className="title">Min</span>
                    </Col>
                    <Col xs={12} sm={10}>
                      <div className="background-input-price">
                        <input
                          type="number"
                          min={0}
                          placeholder="0 GEO$"
                          className={`w-100 ${hasMinPriceError && "invalid"}`}
                          onKeyPress={onKeyPressPrice}
                          onChange={(event) => onChangePrice(event, false)}
                          ref={inputMinPrice}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className="content-price align-items-center justify-content-center">
                    <Col xs={12} sm={2}>
                      <span className="title">Max</span>
                    </Col>
                    <Col xs={12} sm={10}>
                      <div className="background-input-price">
                        <input
                          type="number"
                          min={0}
                          placeholder="0 GEO$"
                          className="w-100"
                          onKeyPress={onKeyPressPrice}
                          onChange={(event) => onChangePrice(event, false)}
                          ref={inputMaxPrice}
                        />
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Col>
              <Col className="time-col">
                <Container className="time-container py-3">
                  <Row className="justify-content-center">
                    <span className="title">Time</span>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <div onChange={(event) => onChangeTime(event)}>
                        <label className="content-input">
                          <input
                            type="radio"
                            value={TIME_OPTIONS.STARTED}
                            name="time"
                            checked={selectedTime === TIME_OPTIONS.STARTED}
                            readOnly
                          />
                          <i></i>
                          <span className="title">Started</span>
                        </label>
                        <label className="content-input">
                          <input
                            type="radio"
                            value={TIME_OPTIONS.START_TODAY}
                            name="time"
                            checked={selectedTime === TIME_OPTIONS.START_TODAY}
                            readOnly
                          />
                          <i></i>
                          <span className="title">Start today</span>
                        </label>
                        <label className="content-input">
                          <input
                            type="radio"
                            value={TIME_OPTIONS.START_THIS_WEEK}
                            name="time"
                            checked={
                              selectedTime === TIME_OPTIONS.START_THIS_WEEK
                            }
                            readOnly
                          />
                          <i></i>
                          <span className="title">Start this week</span>
                        </label>
                        <label className="content-input">
                          <input
                            type="radio"
                            value={TIME_OPTIONS.START_THIS_MONTH}
                            name="time"
                            checked={
                              selectedTime === TIME_OPTIONS.START_THIS_MONTH
                            }
                            readOnly
                          />
                          <i></i>
                          <span className="title">Start this month</span>
                        </label>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
      <Row className="text-center">
        <Col xs={12}>
          <button className="reset" onClick={reset}>
            Reset All
          </button>
          <button
            className="apply"
            onClick={onClickApplyChanges}
            disabled={hasMinPriceError || hasMaxPriceError}
          >
            Apply Changes
          </button>
        </Col>
      </Row>
    </Container>
  );

  return (
    <Modal
      size="lg"
      title="Filters"
      bodyContent={getBodyContent()}
      showFooter={false}
      displayModal={displayModal}
      setDisplayModal={setDisplayModal}
    />
  );
};

export default FilterModal;
